// Countdown to permanent removal for a deleted recording, shown in the "Recently
// deleted" section of the recordings list.
//
// A deleted recording is kept for RETENTION_DAYS and then hard-destroyed by
// RecordingPurgeWorker on the backend. The same window and the same wording are used by
// the apps' "Recently Discarded" lists (DiscardedRecordingRetention on Android,
// DiscardedRecordingsService on iOS), so keep the three in step — a user who deletes on
// their phone and looks on the web should not see two different deadlines.
//
// Kept free of Vue and of `new Date()` defaults in the signatures so the boundaries are
// directly testable: every function takes `now` explicitly.

/** Must match Recording::PURGE_RETENTION_DAYS on the backend. */
export const RETENTION_DAYS = 7

const MS_PER_DAY = 24 * 60 * 60 * 1000

function parseDeletedAt (deletedAt: string | Date | null | undefined): number | null {
  if (deletedAt === null || deletedAt === undefined || deletedAt === '') return null

  const ms = deletedAt instanceof Date ? deletedAt.getTime() : Date.parse(deletedAt)
  return Number.isNaN(ms) ? null : ms
}

/** When the purge becomes eligible to remove this recording, or null if unknown. */
export function purgeDueAt (deletedAt: string | Date | null | undefined): Date | null {
  const ms = parseDeletedAt(deletedAt)
  return ms === null ? null : new Date(ms + RETENTION_DAYS * MS_PER_DAY)
}

/**
 * Whole days left before permanent removal, floored, never negative. 0 means "today" —
 * either the window closes today or it has already closed and the purge has not run yet.
 * Returns null when deletedAt is missing or unparseable, so callers can hide the label
 * rather than render a bogus countdown.
 */
export function daysUntilPurge (
  deletedAt: string | Date | null | undefined,
  now: Date | number,
): number | null {
  const due = purgeDueAt(deletedAt)
  if (due === null) return null

  const nowMs = now instanceof Date ? now.getTime() : now
  const remaining = due.getTime() - nowMs
  if (remaining <= 0) return 0

  return Math.floor(remaining / MS_PER_DAY)
}

/**
 * Human label for the countdown, or null when there is nothing sensible to show.
 * Deliberately says "Deletes" rather than "Expires": the recording is already deleted
 * from the user's point of view, and what the deadline governs is losing it for good.
 */
export function retentionLabel (
  deletedAt: string | Date | null | undefined,
  now: Date | number,
): string | null {
  const days = daysUntilPurge(deletedAt, now)
  if (days === null) return null
  if (days === 0) return 'Deletes today'
  if (days === 1) return 'Deletes tomorrow'

  return `Deletes in ${days} days`
}
